/** Terrella's entry point: wires the clock, the game, the world, the input, the network and the HUD. */
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';

import { createClock } from '@core/clock';
import { createLoop } from '@core/loop';
import { stats } from '@core/stats';
import { makeGrid } from '@bramvasterd/terrella-rules';

import { runDev, submitAction } from '@game/intents/submit';
import { tileFacts } from '@game/planet/tile';
import { startHubCard } from '@game/view/hub';
import { startTilePanels } from '@game/view/panels';
import { createGame } from '@game/game';
import { requestLobby } from '@game/session/lobby';
import { predictTime, rebuildView, startPredictTimer } from '@game/session/predict';
import type { Intent } from '@game/types';
import { detectLocale, getLocale, setLocale, t } from '@i18n/index';
import { attachKeyboard } from '@input/keyboard';
import { attachPointer } from '@input/pointer';
import { createWalker } from '@input/walk';
import { configureApi } from '@net/api';
import { createAuth, settingsFromEnv } from '@net/auth';
import { withGuests } from '@net/guest';
import { startConnection } from '@net/connection';
import { startLobby } from '@net/lobby';
import { onLifecycle } from '@platform/lifecycle';
import { hideSplash, prepareShell } from '@platform/shell';
import { storage } from '@platform/storage';
import { App } from '@ui/App';
import { colorFor } from '@core/palette';
import { createWorld } from '@world/index';

function element(selector: string): HTMLElement {
  const found = document.querySelector<HTMLElement>(selector);
  if (!found) throw new Error(`missing ${selector}`);
  return found;
}

const canvas = element('#world') as HTMLCanvasElement;
const tagsRoot = element('#tags');
const uiRoot = element('#ui');

setLocale(detectLocale());
canvas.setAttribute('aria-label', t('hud.canvas-aria'));
void prepareShell();

const clock = createClock();
const game = createGame({ clock, storage, now: () => performance.now() });
const auth = withGuests(createAuth(settingsFromEnv(), { storage }), { storage });
configureApi({ token: (fresh) => auth.accessToken(fresh) });

let world: ReturnType<typeof createWorld> | null = null;
let frames = 0;
let fpsAt = performance.now();
const loop = createLoop((dt, now, full) => {
  walker.update(dt, now, keyboard.held());
  world?.frame(dt, now, full);
  frames++;
  if (now - fpsAt > 500 && world) {
    const w = world.stats();
    stats.set({
      fps: Math.round((frames * 1000) / (now - fpsAt)),
      frameMs: w.frameMs,
      calls: w.calls,
      triangles: w.triangles,
      quality: w.quality,
    });
    frames = 0;
    fpsAt = now;
  }
});

world = createWorld(
  canvas,
  tagsRoot,
  game,
  loop,
  {
    nameOf: (id) => game.store.get().members.get(id)?.name ?? t('hud.crewmate'),
    myColor: () => game.store.get().me?.color ?? colorFor('me'),
  },
  'stylized',
);
const walker = createWalker(game, world, loop);
const keyboard = attachKeyboard(canvas, game, loop);
attachPointer(canvas, game, world, walker, loop);

startConnection(game, auth);
startLobby(game, auth, {
  language: () => getLocale(),
  firstWorld: (name) => t('world.first-name', { name }),
});
void auth.start();
startPredictTimer(game);
startHubCard(game);
startTilePanels(game);
onLifecycle((active) => {
  if (active) loop.wake(1000);
});

createRoot(uiRoot).render(createElement(App, { game }));
loop.start();
requestAnimationFrame(() => {
  void hideSplash();
});

// Hooks for the end-to-end tests (src/test-hooks.d.ts): act on any tile, send an intent the buttons never offer.
if (import.meta.env.DEV || import.meta.env.VITE_DEV_TOOLS === 'true') {
  window.terrella = {
    game,
    grid: makeGrid,
    facts: (tile) => {
      const s = game.store.get();
      return s.view ? tileFacts(s.view, tile, predictTime(game)) : null;
    },
    submit: (action) => submitAction(game, action),
    sendUnchecked: (action) => {
      const s = game.store.get();
      const intent: Intent = { id: s.nextIntentId, kind: 'action', action };
      game.store.set({ pending: [...s.pending, intent], nextIntentId: intent.id + 1 });
      rebuildView(game);
      game.events.emit('intent', intent);
      return intent.id;
    },
    dev: (command) => runDev(game, command),
    walkTo: (tile) => walker.walkToTile(tile, performance.now()),
    leaveWorld: () => requestLobby(game, { kind: 'leave' }),
  };
}
